import type { ColumnDef } from "@tanstack/react-table";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { RemoveBrokerConnectedAccountDialog, REMOVE_BROKER_ACCOUNT_CONFIRM_PHRASE } from "./RemoveBrokerConnectedAccountDialog";
import { ClearAccountTradeDataDialog, CLEAR_ACCOUNT_TRADES_CONFIRM_PHRASE } from "./ClearAccountTradeDataDialog";
import { Button, type ButtonProps } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { toastError, presentMutationError } from "../../../../utils/showApiErrorToast";
import {
  RefreshCCWIcon,
  type RefreshCCWIconHandle,
} from "@/components/ui/refresh-ccw-icon";
import { SquarePenIcon } from "@/components/ui/square-pen-icon";
import { TrashIcon } from "@/components/ui/trash";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  deleteBrokerAccount,
  updateBrokerAccountCustomName,
} from "@/services/brokerIntegrationApi";
import { deleteAllTrades } from "@/services/tradeApi";
import { notifyTradesUpdated } from "@/hooks/useTradeStatistics";
import { cn } from "@/lib/utils";
import {
  accountSettingsFieldDescriptionClassName,
  accountSettingsInsetSurfaceClassName,
} from "../accountSettingsCardShell";
import { summarizeRemoveAccountResult } from "@/utils/brokerAccountRemoval";
import { EditBrokerAccountNameDialog } from "./EditBrokerAccountNameDialog";
import {
  brokerConnectedAccountConfirmLabel,
  type BrokerConnectedAccountRow,
} from "../brokerConnectedAccountRow";

export type { BrokerConnectedAccountRow } from "../brokerConnectedAccountRow";
export {
  brokerConnectedAccountConfirmLabel,
  formatBrokerConnectedAccountDisplayName,
} from "../brokerConnectedAccountRow";

export interface BrokerConnectedAccountsTableProps {
  brokerName: string;
  accounts: BrokerConnectedAccountRow[];
  activeAccountCode: string | null;
  activeBrokerName: string | null;
  /** Connected (default) or broker-discovered accounts without Tessera data. */
  variant?: "connected" | "available";
  showSyncAction?: boolean;
  syncDisabled?: boolean;
  isAccountSyncDisabled?: (accountCode: string) => boolean;
  onSyncAccount: (accountCode: string) => Promise<void>;
  onAccountRemoved: (brokerName: string, accountCode: string) => Promise<void>;
  onCustomNameUpdated: () => Promise<void>;
  onTradeDataCleared?: () => Promise<void>;
};

type AnimatedIconHandle = {
  startAnimation: () => void;
  stopAnimation: () => void;
};

function useAnimatedIconHoverHandlers(
  iconRef: React.RefObject<AnimatedIconHandle | null>,
  disabled?: boolean
) {
  const startIconAnimation = useCallback(() => {
    if (disabled) return;
    void iconRef.current?.startAnimation();
  }, [disabled, iconRef]);

  return useMemo(
    () =>
      disabled
        ? {}
        : {
            onPointerDownCapture: startIconAnimation,
          },
    [disabled, startIconAnimation]
  );
}

const ACTION_TOOLTIP_CLASS =
  "max-w-[260px] font-[family-name:var(--font-primary)] text-xs leading-snug";

function ActionTooltip({
  label,
  disabled,
  children,
}: {
  label: string;
  disabled?: boolean;
  children: React.ReactElement;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn("inline-flex", disabled && "cursor-not-allowed")}>{children}</span>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={6} className={ACTION_TOOLTIP_CLASS}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function BrokerConnectedAccountIconButton({
  variant = "outline-gray",
  className,
  ariaLabel,
  tooltip,
  disabled,
  onClick,
  children,
}: {
  variant?: ButtonProps["variant"];
  className?: string;
  ariaLabel: string;
  tooltip: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactElement<{ ref?: React.Ref<AnimatedIconHandle> }>;
}) {
  const iconRef = useRef<AnimatedIconHandle | null>(null);
  const hoverHandlers = useAnimatedIconHoverHandlers(iconRef, disabled);

  return (
    <ActionTooltip label={tooltip} disabled={disabled}>
      <Button
        type="button"
        variant={variant}
        size="icon-sm"
        data-font="primary"
        className={cn("shrink-0", className)}
        disabled={disabled}
        aria-label={ariaLabel}
        onClick={onClick}
        {...hoverHandlers}
      >
        {React.cloneElement(children, { ref: iconRef })}
      </Button>
    </ActionTooltip>
  );
}

function BrokerAccountSyncIconButton({
  ariaLabel,
  tooltip,
  disabled,
  isSyncing,
  onClick,
}: {
  ariaLabel: string;
  tooltip: string;
  disabled?: boolean;
  isSyncing?: boolean;
  onClick: () => void;
}) {
  const iconRef = useRef<RefreshCCWIconHandle | null>(null);
  const hoverHandlers = useAnimatedIconHoverHandlers(iconRef, disabled || isSyncing);

  return (
    <ActionTooltip label={tooltip} disabled={disabled || isSyncing}>
      <Button
        type="button"
        variant="gradient"
        size="icon-sm"
        data-font="primary"
        className="shrink-0"
        aria-label={ariaLabel}
        aria-busy={isSyncing || undefined}
        disabled={disabled || isSyncing}
        onClick={onClick}
        {...hoverHandlers}
      >
        {isSyncing ? (
          <Loader2 className="size-4 shrink-0 animate-spin text-white" aria-hidden />
        ) : (
          <RefreshCCWIcon
            ref={iconRef}
            size={16}
            className="pointer-events-none shrink-0 text-white"
            aria-hidden
          />
        )}
      </Button>
    </ActionTooltip>
  );
}

function BrokerAccountClearTradesButton({
  disabled,
  onClick,
}: {
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <ActionTooltip
      label="Remove imported trades only. Credentials and account settings stay connected."
      disabled={disabled}
    >
      <Button
        type="button"
        variant="outline-orange"
        size="sm"
        data-font="primary"
        className="h-8 shrink-0 px-3 normal-case tracking-normal"
        disabled={disabled}
        onClick={onClick}
      >
        Clear trades
      </Button>
    </ActionTooltip>
  );
}

function BrokerAccountReopenButton({
  disabled,
  isSyncing,
  onClick,
}: {
  disabled?: boolean;
  isSyncing?: boolean;
  onClick: () => void;
}) {
  return (
    <ActionTooltip
      label="Add this account and sync trades from the broker."
      disabled={disabled || isSyncing}
    >
      <Button
        type="button"
        variant="gradient"
        size="sm"
        data-font="primary"
        className="h-8 shrink-0 px-3 normal-case tracking-normal"
        disabled={disabled || isSyncing}
        aria-busy={isSyncing || undefined}
        onClick={onClick}
      >
        {isSyncing ? (
          <>
            <Loader2 className="mr-1.5 size-4 shrink-0 animate-spin text-white" aria-hidden />
            Adding…
          </>
        ) : (
          "Add account"
        )}
      </Button>
    </ActionTooltip>
  );
}

type BrokerConnectedAccountActionsCellProps = {
  account: BrokerConnectedAccountRow;
  variant: "connected" | "available";
  isSyncing: boolean;
  actionsDisabled: boolean;
  syncDisabledForRow: boolean;
  showSyncAction: boolean;
  onEdit: (account: BrokerConnectedAccountRow) => void;
  onSync: (accountCode: string) => void;
  onClearTrades: (account: BrokerConnectedAccountRow) => void;
  onRemove: (account: BrokerConnectedAccountRow) => void;
};

const BrokerConnectedAccountActionsCell = React.memo(function BrokerConnectedAccountActionsCell({
  account,
  variant,
  isSyncing,
  actionsDisabled,
  syncDisabledForRow,
  showSyncAction,
  onEdit,
  onSync,
  onClearTrades,
  onRemove,
}: BrokerConnectedAccountActionsCellProps) {
  const isAvailable = variant === "available";

  return (
    <div className="flex items-center justify-end gap-1.5" data-no-row-click="">
      <BrokerConnectedAccountIconButton
        variant="outline-gray"
        ariaLabel={`Edit account name for ${account.displayName}`}
        tooltip="Edit account name"
        disabled={actionsDisabled}
        onClick={() => onEdit(account)}
      >
        <SquarePenIcon
          size={16}
          className="pointer-events-none shrink-0 text-current"
          aria-hidden
        />
      </BrokerConnectedAccountIconButton>
      {isAvailable ? (
        <BrokerAccountReopenButton
          disabled={syncDisabledForRow}
          isSyncing={isSyncing}
          onClick={() => onSync(account.accountCode)}
        />
      ) : (
        <>
          {showSyncAction ? (
            <BrokerAccountSyncIconButton
              ariaLabel={`Resync trades for ${account.displayName}`}
              tooltip="Resync trades from broker"
              disabled={syncDisabledForRow}
              isSyncing={isSyncing}
              onClick={() => onSync(account.accountCode)}
            />
          ) : null}
          <BrokerAccountClearTradesButton
            disabled={actionsDisabled}
            onClick={() => onClearTrades(account)}
          />
          <BrokerConnectedAccountIconButton
            variant="outline-red"
            ariaLabel={`Remove all Tessera data for ${account.displayName}`}
            tooltip="Remove all Tessera data for this account"
            disabled={actionsDisabled}
            onClick={() => onRemove(account)}
          >
            <TrashIcon
              size={16}
              className="pointer-events-none shrink-0 text-current"
              aria-hidden
            />
          </BrokerConnectedAccountIconButton>
        </>
      )}
    </div>
  );
});

const BrokerConnectedAccountsTable: React.FC<BrokerConnectedAccountsTableProps> = ({
  brokerName,
  accounts,
  activeAccountCode,
  activeBrokerName,
  variant = "connected",
  showSyncAction = true,
  syncDisabled = false,
  isAccountSyncDisabled,
  onSyncAccount,
  onAccountRemoved,
  onCustomNameUpdated,
  onTradeDataCleared,
}) => {
  const [syncingAccountCode, setSyncingAccountCode] = useState<string | null>(null);
  const [editNameTarget, setEditNameTarget] = useState<BrokerConnectedAccountRow | null>(null);
  const [editNameValue, setEditNameValue] = useState("");
  const [savingCustomName, setSavingCustomName] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<BrokerConnectedAccountRow | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [clearTarget, setClearTarget] = useState<BrokerConnectedAccountRow | null>(null);
  const [clearConfirmText, setClearConfirmText] = useState("");
  const [isClearing, setIsClearing] = useState(false);
  const onSyncAccountRef = useRef(onSyncAccount);
  const onCustomNameUpdatedRef = useRef(onCustomNameUpdated);
  const isAccountSyncDisabledRef = useRef(isAccountSyncDisabled);

  onSyncAccountRef.current = onSyncAccount;
  onCustomNameUpdatedRef.current = onCustomNameUpdated;
  isAccountSyncDisabledRef.current = isAccountSyncDisabled;

  const activeRowId =
    activeAccountCode && brokerName === activeBrokerName ? activeAccountCode : undefined;

  const closeDeleteDialog = useCallback(() => {
    if (isDeleting) return;
    setDeleteTarget(null);
    setDeleteConfirmText("");
  }, [isDeleting]);

  const closeClearDialog = useCallback(() => {
    if (isClearing) return;
    setClearTarget(null);
    setClearConfirmText("");
  }, [isClearing]);

  const openEditNameDialog = useCallback((account: BrokerConnectedAccountRow) => {
    setEditNameTarget(account);
    setEditNameValue(account.customName?.trim() ?? "");
  }, []);

  const closeEditNameDialog = useCallback(() => {
    if (savingCustomName) return;
    setEditNameTarget(null);
    setEditNameValue("");
  }, [savingCustomName]);

  const saveCustomName = useCallback(async () => {
    if (!editNameTarget) return;
    const trimmed = editNameValue.trim();
    setSavingCustomName(true);
    try {
      await updateBrokerAccountCustomName(brokerName, editNameTarget.accountCode, trimmed || null);
      await onCustomNameUpdatedRef.current();
      window.dispatchEvent(new CustomEvent("accountCustomNameChanged"));
      toast.success("Name saved", {
        description: trimmed ? `Display name set to "${trimmed}".` : "Custom name cleared.",
      });
      setEditNameTarget(null);
      setEditNameValue("");
    } catch (error) {
      presentMutationError(error, "Could not save name");
    } finally {
      setSavingCustomName(false);
    }
  }, [brokerName, editNameTarget, editNameValue]);

  const handleSync = useCallback(async (accountCode: string) => {
    setSyncingAccountCode(accountCode);
    try {
      await onSyncAccountRef.current(accountCode);
    } finally {
      setSyncingAccountCode(null);
    }
  }, []);

  const onClearTrades = useCallback((account: BrokerConnectedAccountRow) => {
    setClearTarget(account);
    setClearConfirmText("");
  }, []);

  const onRemoveAccount = useCallback((account: BrokerConnectedAccountRow) => {
    setDeleteTarget(account);
    setDeleteConfirmText("");
  }, []);

  const confirmRemove = useCallback(async () => {
    if (!deleteTarget) return;
    if (deleteConfirmText.trim().toLowerCase() !== REMOVE_BROKER_ACCOUNT_CONFIRM_PHRASE) return;
    setIsDeleting(true);
    try {
      const result = await deleteBrokerAccount({
        broker_account_code: deleteTarget.accountCode,
        broker_name: brokerName,
      });
      notifyTradesUpdated({ action: "removeBrokerAccount" });
      await onAccountRemoved(brokerName, deleteTarget.accountCode);
      toast.success("Account data removed", {
        description: summarizeRemoveAccountResult(result),
      });
      closeDeleteDialog();
    } catch (error) {
      presentMutationError(error, "Remove failed");
    } finally {
      setIsDeleting(false);
    }
  }, [brokerName, closeDeleteDialog, deleteConfirmText, deleteTarget, onAccountRemoved]);

  const confirmClearTradeData = useCallback(async () => {
    if (!clearTarget) return;
    if (clearConfirmText.trim().toLowerCase() !== CLEAR_ACCOUNT_TRADES_CONFIRM_PHRASE) return;
    setIsClearing(true);
    try {
      await deleteAllTrades(clearTarget.accountCode, brokerName);
      notifyTradesUpdated({
        action: "clearAccountTrades",
        accountCode: clearTarget.accountCode,
        brokerName,
      });
      await onTradeDataCleared?.();
      toast.success("Trade data cleared", {
        description: `Trades for ${clearTarget.displayName} were removed. Resync when you are ready for fresh data.`,
      });
      closeClearDialog();
    } catch (error) {
      presentMutationError(error, "Clear failed");
    } finally {
      setIsClearing(false);
    }
  }, [brokerName, clearConfirmText, clearTarget, closeClearDialog, onTradeDataCleared]);

  const columns = useMemo((): ColumnDef<BrokerConnectedAccountRow>[] => {
    return [
      {
        accessorKey: "displayName",
        header: () => (
          <div className={cn(accountSettingsFieldDescriptionClassName, "font-normal")}>Account</div>
        ),
        cell: ({ row }) => {
          const account = row.original;

          return (
            <div
              data-broker-connected-accounts-name-cell=""
              className="min-w-0 max-w-[min(100%,28rem)] whitespace-normal"
            >
              <p
                data-broker-connected-accounts-name=""
                className={cn(
                  accountSettingsFieldDescriptionClassName,
                  "truncate font-medium",
                )}
                title={account.displayName}
              >
                {account.displayName}
                {account.isDemo ? " (Demo)" : ""}
              </p>
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: "actions",
        header: () => (
          <div
            className={cn(
              accountSettingsFieldDescriptionClassName,
              "text-right font-normal",
            )}
          >
            Actions
          </div>
        ),
        cell: ({ row }) => {
          const account = row.original;
          const isSyncing = syncingAccountCode === account.accountCode;
          const actionsDisabled =
            savingCustomName ||
            isDeleting ||
            isClearing ||
            (syncingAccountCode != null && syncingAccountCode !== account.accountCode);
          const syncDisabledForRow =
            actionsDisabled ||
            syncDisabled ||
            isAccountSyncDisabledRef.current?.(account.accountCode) === true;

          return (
            <BrokerConnectedAccountActionsCell
              account={account}
              variant={variant}
              isSyncing={isSyncing}
              actionsDisabled={actionsDisabled}
              syncDisabledForRow={syncDisabledForRow}
              showSyncAction={showSyncAction}
              onEdit={openEditNameDialog}
              onSync={handleSync}
              onClearTrades={onClearTrades}
              onRemove={onRemoveAccount}
            />
          );
        },
        enableSorting: false,
        enableHiding: false,
        meta: { align: "end" },
      },
    ];
  }, [
    handleSync,
    isClearing,
    isDeleting,
    onClearTrades,
    onRemoveAccount,
    openEditNameDialog,
    savingCustomName,
    showSyncAction,
    syncDisabled,
    syncingAccountCode,
    variant,
  ]);

  const deleteTargetIsActive =
    !!deleteTarget &&
    deleteTarget.accountCode === activeAccountCode &&
    brokerName === activeBrokerName;

  const showPagination = accounts.length > 10;
  const emptyMessage =
    variant === "available"
      ? "No available accounts. After you remove Tessera data for a connected account, it appears here so you can add it."
      : "No connected accounts yet. When your broker accounts appear under Available accounts, use Add account on a row to sync that account.";

  return (
    <>
      <TooltipProvider delayDuration={300} skipDelayDuration={100}>
        <div
          data-broker-connected-accounts-table=""
          className="flex w-full min-w-0 flex-col gap-4"
        >
          <DataTable
            columns={columns}
            data={accounts}
            getRowId={(row) => row.accountCode}
            activeRowId={activeRowId}
            emptyMessage={emptyMessage}
            showPagination={showPagination}
            paginationAfterShell={showPagination}
            showColumnVisibility={false}
            tableDataAttributes={{ "data-account-settings-broker-accounts-table": "" }}
            tableClassName={cn(
              "bg-transparent shadow-none",
              accountSettingsInsetSurfaceClassName,
            )}
          />
        </div>
      </TooltipProvider>

      <EditBrokerAccountNameDialog
        target={
          editNameTarget
            ? {
                accountCode: editNameTarget.accountCode,
                displayName: editNameTarget.displayName,
              }
            : null
        }
        value={editNameValue}
        onValueChange={setEditNameValue}
        isSaving={savingCustomName}
        onCancel={closeEditNameDialog}
        onSave={() => void saveCustomName()}
      />

      <RemoveBrokerConnectedAccountDialog
        target={
          deleteTarget
            ? { displayName: brokerConnectedAccountConfirmLabel(deleteTarget) }
            : null
        }
        confirmText={deleteConfirmText}
        onConfirmTextChange={setDeleteConfirmText}
        isActiveInSidebar={deleteTargetIsActive}
        isDeleting={isDeleting}
        onCancel={closeDeleteDialog}
        onConfirm={() => void confirmRemove()}
      />

      <ClearAccountTradeDataDialog
        target={
          clearTarget
            ? { displayName: brokerConnectedAccountConfirmLabel(clearTarget) }
            : null
        }
        confirmText={clearConfirmText}
        onConfirmTextChange={setClearConfirmText}
        isClearing={isClearing}
        onCancel={closeClearDialog}
        onConfirm={() => void confirmClearTradeData()}
      />
    </>
  );
};

export default BrokerConnectedAccountsTable;
